import React, { useMemo, useEffect, useState } from 'react'
import VisualTopicSelector, { useVisualTopicSelector } from '../../../visualTopicSelector/index'
import NoTopicsMessage from '@/components/overview/noTopicsMessage'
import ViewSelector from '../../../visualTopicSelector/viewSelector'
import PublicationTopicRow from './publicationTopicRow'
import { Tabs, Panel } from 'rsuite'
import localStyles from './publication.module.scss'
import useTranslation from '@/hooks/useTranslation'
import { useIsMobile } from '@/hooks/useIsMobile'
import IcoMoonIcon from '@/components/themes/common/icons/IcoMoonIcon'
import ICON_MAP from '@/components/themes/common/icons/iconMapper'
import { useSelector } from 'react-redux'
import { configSelectors } from '@/store/configs'
import { customerHelper } from '@tweddle-se-team/common/constants'
import NordicsTopics from '@/components/themes/subaru-nordics/publications/visualTopics'

const { CUSTOMER_IDS } = customerHelper

const HOTSPOT_HELP_TEXT = 'Select a dot on the image to view more information about that area.'

/**
 * Subaru-specific Hotspots implementation
 * TODO: Customize with Subaru-specific styling and behavior
 */
const InfoMessage = ({ onClose, t }) => {
  return (
    <div className={localStyles.infoMessage}>
      <IcoMoonIcon className={localStyles.infoMessageIcon} icon={ICON_MAP.calendarExclamation} />
      <span className={localStyles.infoMessageText} id="hotspot-help-text">
        {t('hotspot-help-text', HOTSPOT_HELP_TEXT)}
      </span>
      <button
        type="button"
        className={localStyles.infoMessageCloseButton}
        aria-label={t('close_help_text', 'Close help text')}
        onClick={onClose}
      >
        <IcoMoonIcon icon={ICON_MAP.close} />
      </button>
    </div>
  )
}

const SubaruHotspots = () => {
  const { selectedView, onSelectView, hotSpotMapping } = useVisualTopicSelector()
  const [showInfoMessage, setShowInfoMessage] = useState(true)
  const isMobile = useIsMobile()
  const t = useTranslation()

  return (
    <>
      {showInfoMessage && isMobile && <InfoMessage onClose={() => setShowInfoMessage(false)} t={t} />}
      <ViewSelector
        selectedView={selectedView}
        onSelectView={onSelectView}
        hotSpotMapping={hotSpotMapping}
        className={localStyles.secondaryTabs}
        variant="transparent"
        additionalImageAltText={t('hotspot-help-text', HOTSPOT_HELP_TEXT)}
      />
      {showInfoMessage && !isMobile && <InfoMessage onClose={() => setShowInfoMessage(false)} t={t} />}
    </>
  )
}

const TopicTypeSelector = ({
  topicTypes = [],
  className,
  selectedTopicType,
  onSelectTopicType,
  filteredTopics,
  publicationRowClassName,
  onTopicSelect,
  publicationListClassName,
  ...navProps
}) => {
  const t = useTranslation()
  return (
    <Tabs
      className={className}
      activeKey={selectedTopicType}
      appearance="subtle"
      onSelect={selectedTopicType => onSelectTopicType({ selectedTopicType })}
      aria-label="Filter topics by type"
      {...navProps}
    >
      {topicTypes?.map(topicType => (
        <Tabs.Tab
          key={topicType}
          eventKey={topicType}
          title={t(`digital_first_topic_type_${topicType.toLowerCase().replace(/\s+/g, '_')}`)}
        >
          <Panel className={publicationListClassName}>
            {filteredTopics[topicType]?.map(topic => (
              <PublicationTopicRow
                key={topic.id}
                type={topic.type}
                title={topic.title}
                onClick={() => onTopicSelect(topic)}
                className={publicationRowClassName}
              />
            ))}
          </Panel>
        </Tabs.Tab>
      ))}
    </Tabs>
  )
}

/**
 * Subaru-specific Topics implementation
 * Displays topics filtered by selectedTopicType
 */
const SubaruTopics = () => {
  const {
    hotspotForCurrentView,
    partTopics,
    onSelectTopicType,
    selectedTopicType,
    onTopicSelect,
    onTabChange,
    publicationRowClassName,
    publicationListClassName
  } = useVisualTopicSelector()

  // Filter topics based on each topic type
  const filteredTopics = useMemo(() => {
    if (!partTopics) return { all: [], topic: [], video: [] }

    return {
      all: partTopics,
      topic: partTopics.filter(topic => topic.type === 'topic'),
      video: partTopics.filter(topic => topic.type === 'video')
    }
  }, [partTopics])

  // Build available topic types based on content
  const topicTypes = useMemo(() => {
    const types = ['all', 'topic']
    if (filteredTopics.video.length > 0) {
      types.push('video')
    }
    return types
  }, [filteredTopics.video.length])

  // Validate selectedTopicType - use first available if invalid
  const validatedTopicType = useMemo(() => {
    return topicTypes.includes(selectedTopicType) ? selectedTopicType : topicTypes[0]
  }, [selectedTopicType, topicTypes])

  // Automatically update URL if the selected topic type is invalid
  useEffect(() => {
    if (validatedTopicType !== selectedTopicType) {
      onSelectTopicType({ selectedTopicType: validatedTopicType })
    }
  }, [validatedTopicType, selectedTopicType, onSelectTopicType])

  return (
    <>
      {hotspotForCurrentView?.photo ? (
        <div>
          <TopicTypeSelector
            topicTypes={topicTypes}
            selectedTopicType={validatedTopicType}
            onSelectTopicType={onSelectTopicType}
            filteredTopics={filteredTopics}
            publicationRowClassName={publicationRowClassName}
            onTopicSelect={onTopicSelect}
            publicationListClassName={publicationListClassName}
            className={localStyles.secondaryTabs}
            justified
          />
        </div>
      ) : (
        <NoTopicsMessage onLinkClick={() => onTabChange('manuals')} />
      )}
    </>
  )
}

const TopicsByCustomer = {
  [CUSTOMER_IDS.SUBARU]: SubaruTopics,
  [CUSTOMER_IDS.NORDICS]: NordicsTopics
}

/**
 * Subaru Visual Topic Selector
 * Complete implementation for Subaru customer
 */
const SubaruVisualTopicSelector = props => {
  const customerId = useSelector(configSelectors.selectCustomerId)
  const TopicsComponent = TopicsByCustomer[customerId] || TopicsByCustomer[CUSTOMER_IDS.SUBARU]

  return (
    <VisualTopicSelector {...props}>
      <VisualTopicSelector.Hotspots>
        <SubaruHotspots />
      </VisualTopicSelector.Hotspots>
      <VisualTopicSelector.Topics>
        <TopicsComponent />
      </VisualTopicSelector.Topics>
    </VisualTopicSelector>
  )
}

export default SubaruVisualTopicSelector
